import React, { FC, useCallback, useMemo, useRef } from 'react';

import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { Container, Modal } from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { scroller } from 'react-scroll';

import PlaySmallIcon from '../../assets/icons/PlaySmallIcon';
import { ExpertsBlock } from '../../components/ExpertsBlock';
import JoinNowBlock from '../../components/JoinNowBlock';
import { Loader } from '../../components/Loader';
import { PartnersBlock } from '../../components/PartnersBlock';
import { SeoBlock } from '../../components/SeoBlock';
import { IconButton } from '../../components/UI/IconButton';
import { RM_TAGS } from '../../const';
import { useRmTag } from '../../context/RmContext';
import { GET_COURSE, GET_LIST_COURSES_LOGGED_OUT, START_COURSE } from '../../graphql/courses';
import { GET_PAGE_BY_URL } from '../../graphql/pages';
import {
  Course,
  COURSE_STATUSES,
  GetCourseArgs,
  GetPageByUrlArgs,
  LearnerInfo,
  Mutation,
  MutationStartCourseArgs,
  Query,
  Tenant,
  VideoDifficulty,
} from '../../types';
import { COURSE_PAGE_CUSTOM_BLOCKS_URL } from '../../utils/const';
import { getUniqObjectsFromArray } from '../../utils/helpers';
import { useModal } from '../../utils/hooks';
import useVerse from '../../utils/hooks/useVerse';
import { CustomPage } from '../CustomPage';
import JoinNowModal from '../SingleVideoPage/components/JoinNowModal';
import { AboutCourseBlock } from './components/AboutCourseBlock';
import { CourseHeader } from './components/CourseHeader';
import CoursesPathways from './components/CoursePathways/CoursePathways';
import { HeaderUspBlock } from './components/HeaderUspBlock';
import { InfoBlock } from './components/InfoBlock';
import { LearnInfoBlock } from './components/LearnInfoBlock';
import { StartCourseBtn } from './components/StartCourseBtn';
import TryCourses from './components/TryCourses/TryCourses';
import useStyles from './styles';

const partnersConfig = {
  type: 'partners',
  ranking: 5,
  white_icon: false,
  background: 'Dark',
  display: 'Slider',
};

export const checkIsCourseStarted = (
  courseId: string | undefined,
  coursesProgress: Course[] | undefined,
) => {
  return Boolean(
    courseId &&
      coursesProgress?.find(
        (item) =>
          item?.id === courseId && item?.status && item?.status !== COURSE_STATUSES.NOT_STARTED,
      ),
  );
};

interface CourseDetailsPageProps {
  tenant?: Tenant | null;
  userInfo?: LearnerInfo | null;
  width: Breakpoint;
  coursesProgress?: Course[];
  hasAnotherCourses: boolean;
  refetchCourseProgressData: (
    variables?: Record<string, any> | undefined,
  ) => Promise<ApolloQueryResult<Pick<Query, 'listCoursesLogedIn'>>>;
}

const CourseDetailsPage: FC<CourseDetailsPageProps> = ({
  tenant,
  userInfo,
  coursesProgress,
  refetchCourseProgressData,
  hasAnotherCourses,
}) => {
  const classes = useStyles();
  const circleImageUrl = tenant?.circle_image_url;
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const params = useParams();

  useRmTag(RM_TAGS.CoursePage);

  const videoFrameRef = useRef<HTMLFrameElement>(null);
  const { play } = useVerse(videoFrameRef.current?.contentWindow);

  const { openModal, handleOpenModal, handleCloseModal } = useModal();

  const { data: courseData, loading: courseLoading } = useQuery<
    Pick<Query, 'getCourse'>,
    GetCourseArgs
  >(GET_COURSE, {
    variables: {
      slug: params?.slug,
    },
  });

  const { data: coursesList } = useQuery<Pick<Query, 'listCoursesLogedOut'>>(
    GET_LIST_COURSES_LOGGED_OUT,
  );

  const otherCoursesList = useMemo(() => {
    let list: Course[] = [];

    if (coursesList?.listCoursesLogedOut && courseData?.getCourse) {
      list = coursesList.listCoursesLogedOut.filter(
        (item) => item && item?.id !== courseData?.getCourse?.id,
      ) as Course[];
    }

    return list;
  }, [coursesList, courseData]);

  const [startCourse] = useMutation<Pick<Mutation, 'startCourse'>, MutationStartCourseArgs>(
    START_COURSE,
    {
      onCompleted: async () => {
        await refetchCourseProgressData();
        history.push(location.pathname + '/progress');
      },
    },
  );

  const course = courseData?.getCourse;

  const difficulties = useMemo(() => {
    if (courseData?.getCourse) {
      const arr: VideoDifficulty[] = courseData.getCourse?.pathways
        ? (courseData.getCourse?.pathways
            .map((item) => item?.difficulty)
            .filter((item) => Boolean(item)) as VideoDifficulty[])
        : [];

      return getUniqObjectsFromArray(arr, (item) => item?.id);
    }

    return [];
  }, [courseData]);

  const partnersData = useMemo(() => {
    const course = courseData?.getCourse;

    return {
      ...partnersConfig,
      heading: t('page.course.partnersTitle'),
      partnersList: course?.partners?.map((item) => ({
        id: item?.image_url,
        title: item?.alt,
        image_url: item?.image_url,
      })),
    };
  }, [t, courseData]);

  const expertsBlockData = useMemo(() => {
    return {
      type: 'experts',
      heading: t('page.course.expertsTitle'),
      description: t('page.course.expertsDescription'),
      ranking: 11,
      background: 'Light',
      items: courseData?.getCourse?.experts
        ? courseData.getCourse.experts.map((item) => ({
            expert: item,
          }))
        : [],
    };
  }, [t, courseData]);

  const { data: pageData, loading: pageLoading } = useQuery<
    Pick<Query, 'getPageByUrl'>,
    GetPageByUrlArgs
  >(GET_PAGE_BY_URL, {
    variables: {
      url: COURSE_PAGE_CUSTOM_BLOCKS_URL,
    },
    skip: !userInfo?.has_subscription,
  });

  const isCourseStarted = useMemo(
    () => checkIsCourseStarted(courseData?.getCourse?.id, coursesProgress),
    [coursesProgress, courseData],
  );

  const blocks = useMemo(() => {
    const blocksArr = pageData?.getPageByUrl?.blocks;

    return blocksArr ? blocksArr.filter((item) => item?.type !== 'seo-block') : [];
  }, [pageData]);

  const handleStartCourse = useCallback(() => {
    if (userInfo?.has_subscription) {
      if (!isCourseStarted) {
        startCourse({
          variables: {
            course_id: Number(courseData?.getCourse?.id) || 0,
          },
        });
      }
    } else {
      handleOpenModal();
    }
  }, [userInfo, handleOpenModal, isCourseStarted, courseData, startCourse]);

  const handleClickWatchIntroductionVideo = () => {
    scroller.scrollTo('introductionVideo', {
      duration: 1000,
      delay: 0,
      smooth: 'easeInOutQuart',
      offset: -80,
    });

    play();
  };

  if (courseLoading || pageLoading) {
    return (
      <>
        {course && (
          <SeoBlock
            data={{
              seo_title: course?.name,
              seo_description: course?.description,
              share_image: course?.image_url,
            }}
          />
        )}
        <Loader />
      </>
    );
  }

  return (
    <>
      <SeoBlock
        data={{
          seo_title: course?.name,
          seo_description: course?.description,
          share_image: course?.image_url,
        }}
      />
      <div className={classes.wrapper}>
        <CourseHeader
          hasAnotherCourses={hasAnotherCourses}
          title={course?.name}
          description={course?.description}
          circleImageUrl={tenant?.circle_image_url}
          courseIcon={course?.svg_icon}
          courseImgUrl={course?.image_url}
          tenantName={tenant?.name}
        />
        <Container>
          <div className={classes.buttonsBlock}>
            <StartCourseBtn
              isCourseStarted={isCourseStarted}
              handleStartCourse={handleStartCourse}
              className={classes.startCourseBtn}
            />
            {course?.intro_verse_video_id && (
              <>
                <div className={classes.divider} />
                <IconButton
                  onClick={handleClickWatchIntroductionVideo}
                  btnClass={classes.playBtn}
                  icon={<PlaySmallIcon color={'currentColor'} className={classes.playIcon} />}
                  label={t('page.course.watchIntroductionVideo')}
                />
              </>
            )}
          </div>
          {course?.usp_blocks && <HeaderUspBlock uspBlocks={course.usp_blocks} />}
        </Container>
        <InfoBlock
          pathwaysCount={course?.general_info?.pathways}
          cpdCredits={course?.general_info?.cpd_credits}
          learningHours={course?.general_info?.learning_hours}
          expertsCount={course?.general_info?.experts}
          difficulties={difficulties}
        />
        <AboutCourseBlock
          description={course?.about_course}
          verseId={course?.intro_verse_video_id}
          videoFrameRef={videoFrameRef}
          handleStartCourse={handleStartCourse}
          isCourseStarted={isCourseStarted}
        />
        {!userInfo?.has_subscription && (
          <JoinNowBlock
            isLoggedIn={Boolean(userInfo)}
            classes={{
              wrapper: classes.joinNowBlockWrapper,
              container: classes.joinNowBlockContainer,
            }}
          />
        )}
        <LearnInfoBlock
          learnItems={course?.key_learning_objectives}
          whatIsItForContent={course?.who_is_this_for}
          requirementsContent={course?.requirements}
        />
        {course?.pathways && (
          <CoursesPathways
            pathways={course?.pathways}
            isCourseStarted={isCourseStarted}
            handleStartCourse={handleStartCourse}
          />
        )}
        <ExpertsBlock data={expertsBlockData} forceDarkOnMobile={false} />
        <PartnersBlock data={partnersData} preventLoader={false} />
        {/*TODO remove check after adding recommended pathways*/}
        {Boolean(otherCoursesList?.length) && (
          <TryCourses coursesList={otherCoursesList} circleImageUrl={circleImageUrl} />
        )}
        {blocks && blocks.length > 0 && <CustomPage blocks={blocks} />}
      </div>
      {!userInfo?.has_subscription && (
        <Modal className={classes.modal} open={openModal}>
          <JoinNowModal handleClose={() => handleCloseModal()} isLoggedIn={Boolean(userInfo)} />
        </Modal>
      )}
    </>
  );
};

export default CourseDetailsPage;
